import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  TypographyProps,
} from '@mui/material';
import HomeHeader from '../Home/components/HomeHeader';
import BasicLoader from '../../components/Loaders/BasicLoader';

import { SubjectColors, TP_COLORS } from '../../constants';
import { useAppSelector } from '../../hooks';
import { getMatchingMetaTags, getUrlConvertedString } from '../../helper';
import {
  getCurriculumsForHomePage,
  getCurriculumState,
  ICurriculumForHome,
  ISubjectForHome,
} from '../../store/CurriculumSlice';
import { IGroupedSubTopic } from '../../store/SubTopicSlice';
import { getTopicState, getTopicsWithSubTopics } from '../../store/TopicSlice';
import { getTenantsState } from '../../store/V2/TenantsSlice';
import { getMetaTagsState } from '../../store/V2/MetaTagsSlice';

interface IState {
  selectedCurriculum: ICurriculumForHome | undefined;
  selectedSubject: ISubjectForHome | undefined;
}

const initialState: IState = {
  selectedCurriculum: undefined,
  selectedSubject: undefined,
};

const titleTextProps: TypographyProps = {
  variant: 'h1',
  style: {
    fontWeight: 600,
  },
  sx: {
    marginBottom: '30px',
    fontSize: {
      xs: '18px',
      sm: '22px',
      md: '28px',
      lg: '30px',
      xl: '32px',
    },
  },
};

const SubTopicListPage: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Store
  const curriculumState = useAppSelector(getCurriculumState);
  const topicState = useAppSelector(getTopicState);
  const tenantState = useAppSelector(getTenantsState);
  const metaTagState = useAppSelector(getMetaTagsState);

  const { appTenant } = tenantState;
  const { metaTagListData } = metaTagState;
  const { topicsWithSubTopics, status } = topicState;
  const { curriculumsForHomePage } = curriculumState;

  // State
  const [state, setState] = useState<IState>(initialState);

  const tenantName = appTenant?.name || '';
  const matchingMetaTag = getMatchingMetaTags(
    location.pathname.replace(/[/]$/, ''),
    metaTagListData?.list || []
  );

  useEffect(() => {
    if (state.selectedCurriculum && state.selectedSubject) {
      dispatch(
        getTopicsWithSubTopics({
          curriculum: state.selectedCurriculum.name,
          subject: state.selectedSubject.name,
        })
      );
    }
  }, [state]);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      let selectedCurriculum = curriculumsForHomePage.find(
        (curriculumItem) =>
          getUrlConvertedString(curriculumItem.name) === params.curriculum
      );
      if (!selectedCurriculum) {
        selectedCurriculum = { ...curriculumsForHomePage[0] };
      }

      if (selectedCurriculum.subjects?.length) {
        let selectedSubject = selectedCurriculum.subjects.find(
          (subjectItem) =>
            getUrlConvertedString(subjectItem.name) === params.subject
        );
        if (!selectedSubject) {
          selectedSubject = { ...selectedCurriculum.subjects[0] };
        }

        setState((prevState) => ({
          ...prevState,
          selectedCurriculum,
          selectedSubject,
        }));
      }
    }
  }, [params, curriculumsForHomePage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    dispatch(getCurriculumsForHomePage());
  }, []);

  const handleButtonClick = (subTopic: IGroupedSubTopic) => {
    const curriculumName = getUrlConvertedString(
      state.selectedCurriculum?.name || ''
    );
    const subjectName = getUrlConvertedString(
      state.selectedSubject?.name || ''
    );
    const subTopicName = getUrlConvertedString(subTopic.name || '');

    navigate(
      `${process.env.PUBLIC_URL}/${curriculumName}/${subjectName}/${subTopicName}_${subTopic._id}`
    );
  };

  let metaTitle = `IGCSE And GCSE - Maths And Science Questions with Answers By Topic | ${tenantName}`;

  let metaDesc =
    'Science and Maths questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!';

  if (state.selectedCurriculum && state.selectedSubject) {
    metaTitle = `${state.selectedCurriculum.name || ''} - ${
      state.selectedSubject.name || ''
    } Questions with Answers By Topic | ${tenantName}`;

    metaDesc = `${state.selectedCurriculum.name || ''} ${
      state.selectedSubject.name || ''
    } questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!`;
  }

  return (
    <Box>
      {matchingMetaTag &&
      (matchingMetaTag.title || matchingMetaTag.description) ? (
        <Helmet>
          {matchingMetaTag.title ? (
            <title>{matchingMetaTag.title}</title>
          ) : null}
          {matchingMetaTag.description ? (
            <meta name="description" content={matchingMetaTag.description} />
          ) : null}
        </Helmet>
      ) : null}
      <HomeHeader />
      <Box
        sx={{
          minHeight: {
            xs: '200px',
            lg: '350px',
          },
          maxHeight: {
            xs: '200px',
            lg: '350px',
          },
          background: {
            xs: `url('${process.env.PUBLIC_URL}/images/shapes/quiztopiya-rocket.png') center 70px/150px no-repeat border-box border-box fixed, ${SubjectColors[5]}`,
            lg: `url('${process.env.PUBLIC_URL}/images/shapes/quiztopiya-rocket.png') calc(100vw - 350px) 80px/200px no-repeat border-box border-box fixed, url('${process.env.PUBLIC_URL}/images/shapes/cloud-top-right-corner.png') top right/600px no-repeat border-box border-box fixed, ${SubjectColors[5]}`,
          },
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Toolbar />
        <Box
          sx={{
            paddingLeft: '60px',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <div>
            <Typography
              component="h1"
              variant="h4"
              fontWeight={600}
              color={TP_COLORS.white}
            >
              {tenantName} Learning Platform
            </Typography>
          </div>
          <div>
            <Typography
              component="h2"
              variant="subtitle2"
              fontWeight={400}
              color={TP_COLORS.white}
              style={{ opacity: 0.9 }}
            >
              Resources that can help you Boost your Grades
              <br />
              Study Material & Over 50,000 questions for Complete Exam
              Preparation
            </Typography>
          </div>
        </Box>
      </Box>

      <div id="home-container">
        <Box
          sx={{
            background: {
              xs: `url('${process.env.PUBLIC_URL}/images/shapes/cloud-white-gray-combined.png') bottom center/300px repeat-x border-box  border-box scroll, url('${process.env.PUBLIC_URL}/images/shapes/cloud-white.png') bottom left/300px repeat-x`,
              lg: `url('${process.env.PUBLIC_URL}/images/shapes/cloud-white-gray-combined.png') bottom center/400px repeat-x border-box  border-box scroll, url('${process.env.PUBLIC_URL}/images/shapes/cloud-white.png') bottom left/400px repeat-x`,
            },
            minHeight: '100px',
            marginTop: '-99px',
          }}
        />

        <div style={{ minHeight: '80vh' }}>
          {/* Mobile top section */}
          <Box
            sx={{
              display: { xs: 'block', lg: 'none' },
              p: '0 15px',
              m: '15px 0',
            }}
          >
            <div style={{ marginBottom: '10px' }}>
              <Typography component="h1" variant="h6" fontWeight={600}>
                {tenantName} Learning Platform
              </Typography>
            </div>
            <div>
              <Typography
                component="h2"
                variant="subtitle2"
                color="secondary"
                fontWeight={600}
              >
                Resources that can help you Boost your Grades
                <br />
                Study Material & Over 50,000 questions for Complete Exam
                Preparation
              </Typography>
            </div>
          </Box>
          <div>
            {status === 'loading' ? <BasicLoader /> : null}
            <Container maxWidth="lg" sx={{ mt: '40px', mb: '30px' }}>
              <Typography {...titleTextProps}>
                {state.selectedCurriculum?.name} {state.selectedSubject?.name}
              </Typography>
              {topicsWithSubTopics.length ? (
                <Grid container spacing={4}>
                  {topicsWithSubTopics.map((topicItem, topicIndex) => (
                    <Grid key={topicIndex} item xs={12} sm={6} md={4}>
                      <Typography
                        component="h2"
                        variant="h6"
                        fontWeight={600}
                        style={{ marginBottom: '5px' }}
                      >
                        {topicItem.sortingOrder || 0}. {topicItem.name}
                      </Typography>
                      <Paper elevation={2}>
                        <List dense>
                          {topicItem.subTopics &&
                            topicItem.subTopics.length &&
                            topicItem.subTopics.map(
                              (subTopicItem, subTopicIndex) => (
                                <ListItem key={subTopicIndex} disablePadding>
                                  <ListItemButton
                                    onClick={() =>
                                      handleButtonClick(subTopicItem)
                                    }
                                  >
                                    <ListItemText
                                      primary={`${
                                        topicItem.sortingOrder || 0
                                      }.${subTopicItem.sortingOrder || 0} ${
                                        subTopicItem.name
                                      }`}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )
                            )}
                        </List>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : null}
            </Container>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default SubTopicListPage;
